import React, {Suspense, useEffect} from "react";
import {Box, Button, CircularProgress, Container, Grid2, Typography, useTheme} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {useAuth} from "@/app/auth/AuthContext";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {AppPaths} from "@/app/AppPaths";
import {useTranslation} from "react-i18next";
import {AuthI18N} from "@/translations";
import {BoxHubZeichen} from "@/agentur/AgenturIcons";
import {color_grey} from "@/agentur/AgenturColors";
import {StatusFooter} from "@/app/footer/StatusFooter";
import {compactValue} from "@/lib/screen-class";
import PageLoadingSpinner from "@/lib/components/PageLoadingSpinner";
import UserAuthForm from "@/pages/login-page/UserAuthForm";
import {resolveRedirectDestination} from "@/app/auth/signInRedirect";
import {VisitorLanguageSwitch} from "@/app/locale/VisitorLanguageSwitch";

/** Link back to the landing page; on the desktop it heads the video column, below md the form column. */
const BackToLandingButton = () => {
    const { t } = useTranslation();
    return (
        <Button component={Link} to={AppPaths.LANDING_PAGE} startIcon={<ArrowBackIosIcon />} data-cy="sign-in-back">
            {t(AuthI18N.login.action.goToHome)}
        </Button>
    );
};

/** Left column with the way back and the animation, shown from md upwards. */
const SignInVideoColumn = () => {
    const theme = useTheme();
    return (
        <Grid2
            size={{ xs: 12, md: 6, lg: 8 }}
            display={{ xs: "none", md: "flex" }}
            container
            direction="column"
            flexWrap="nowrap"
            height="100%"
            minHeight="100%"
            sx={{ backgroundColor: theme.palette.mode === "light" ? color_grey[4] : undefined }}
        >
            <Grid2 py={2}>
                <BackToLandingButton />
            </Grid2>
            <Grid2 alignItems="center" container flexGrow={1} overflow="hidden">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{
                        width: "100%",
                        pointerEvents: "none",
                        userSelect: "none",
                        objectFit: "cover"
                    }}
                >
                    <source src="/assets/boxhub-animation.webm" type="video/webm" />
                </video>
            </Grid2>
        </Grid2>
    );
};

/**
 * The page heading, h5 below md and h4 from md. The responsive typography sets its own weight and spacing per
 * breakpoint, so the heading's own values follow it at the same breakpoints to keep winning.
 */
const SignInHeading = () => {
    const { t } = useTranslation();
    return (
        <Typography
            variant="h4"
            color="text.primary"
            gutterBottom
            sx={{
                typography: compactValue("h5", "h4"),
                fontWeight: compactValue(600, 600),
                letterSpacing: compactValue("-0.01562em", "-0.01562em"),
                overflowWrap: "break-word"
            }}
        >
            {t(AuthI18N.login.page.loginMessage)}
        </Typography>
    );
};

/** Top padding of the form column at md, in spacing units: the switch's 16 px offset and 36 px height plus a gap. */
const SWITCH_CLEARANCE = 8;

/**
 * First row of the form column: below md the way back on the left and the language switch on the right. From md the
 * switch is pinned to the top right corner of the column instead: in the flow of the vertically centred column it ends
 * up above the window on low screens, such as 1280 x 720.
 */
const SignInTopRow = () => (
    <Box
        display="flex"
        alignItems="center"
        width={{ xs: "100%", md: "auto" }}
        position={{ md: "absolute" }}
        top={{ md: 16 }}
        right={{ md: 16 }}
    >
        <Box display={{ xs: "block", md: "none" }}>
            <BackToLandingButton />
        </Box>
        <Box marginLeft="auto">
            <VisitorLanguageSwitch />
        </Box>
    </Box>
);

/** The column with the sign-in form; it is the only column below md. */
const SignInFormColumn = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    return (
        <Grid2
            size={{ xs: 12, md: 6, lg: 5 }}
            container
            direction="column"
            spacing={2}
            alignItems="center"
            justifyItems="center"
            flexWrap="nowrap"
            sx={{
                position: "relative",
                // md only: the top padding keeps the pinned language switch clear of the form, and "safe center" lets
                // a column taller than a low window overflow downwards, where the page scrolls, instead of upwards.
                justifyContent: { xs: "center", md: "safe center", lg: "center" },
                pt: { md: SWITCH_CLEARANCE, lg: 0 },
                px: {
                    xs: 1,
                    md: 4,
                    lg: 12
                }
            }}
        >
            <SignInTopRow />
            <Grid2 paddingBottom={2}>
                <BoxHubZeichen mode={theme.palette.mode} />
            </Grid2>
            <Grid2>
                <SignInHeading />
            </Grid2>
            <Grid2>
                <Typography variant="body2" color="text.secondary" paddingBottom={2}>
                    {t(AuthI18N.login.page.description)}
                </Typography>
            </Grid2>
            <Grid2 width="100%">
                <Suspense fallback={<CircularProgress />}>{<UserAuthForm />}</Suspense>
            </Grid2>
            <Grid2 width="100%" paddingTop={2}>
                <StatusFooter />
            </Grid2>
        </Grid2>
    );
};

export const SignInPage = () => {
    const { authenticated, loading, isLoggingOut } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const redirectUrl = resolveRedirectDestination(searchParams.get("redirect"), AppPaths.BIDDER_OVERVIEW);
    const navigate = useNavigate();

    // Remove redirect parameter if user is logging out
    useEffect(() => {
        if (isLoggingOut && searchParams.has("redirect")) {
            setSearchParams({});
        }
    }, [isLoggingOut, searchParams, setSearchParams]);

    useEffect(() => {
        if (!loading && authenticated) {
            navigate(redirectUrl);
        }
    }, [authenticated, navigate, redirectUrl, loading]);

    if (loading) {
        return <PageLoadingSpinner />;
    }

    if (authenticated) {
        return null;
    }

    return (
        // Below md the page grows with its content and scrolls as a whole: with a fixed height the centred column
        // pushed the heading above the window in landscape (800x360). The minimum height keeps short content
        // vertically centred, since the column stretches to the row's height.
        <Container disableGutters maxWidth="xl" sx={{ height: compactValue("auto", "100vh") }}>
            <Grid2
                container
                direction="row"
                width="100%"
                spacing={2}
                flexWrap="nowrap"
                sx={{ height: compactValue("auto", "100%"), minHeight: compactValue("100vh", "auto") }}
            >
                <SignInVideoColumn />
                <SignInFormColumn />
            </Grid2>
        </Container>
    );
};
